/**
 * The organization step's location state and checks (SPA side). The rules are
 * the server's (`expert/registration_sync.py`): this only checks them before
 * saving so the expert sees the error under the field.
 */
import type { TerritoryRegion } from "../types";

/** A place of business being edited; `key` only keeps React rows stable. */
export interface LocationRow {
  key: string;
  /** Optional, like the head office's — the region and MRC are required. */
  address: string;
  region: string;
  mrc: string;
}

export type LocationErrors = {
  head_office_region?: string;
  head_office_mrc?: string;
  /** Per place, by index. */
  places?: Record<number, { region?: string; mrc?: string }>;
};

let rowSeq = 0;
export function newLocationRow(region = "", mrc = "", address = ""): LocationRow {
  rowSeq += 1;
  return { key: `place-${rowSeq}`, address, region, mrc };
}

/**
 * Drop the places left entirely empty, as the Django step does
 * (`_parse_business_locations`), so a spare row never blocks the save.
 */
export function dropBlankPlaces(places: LocationRow[]): LocationRow[] {
  // The address counts: a row carrying only a street is half-filled, not
  // blank, and must survive to report its missing region and MRC. Trimmed
  // first, because the Django step strips each posted value before deciding
  // the row is empty — otherwise a row holding only spaces blocks the save
  // in one app and vanishes in the other.
  return places.filter(
    ({ address, region, mrc }) =>
      address.trim() !== "" || region !== "" || mrc !== "",
  );
}

/** The same rules the server applies (registration_sync), checked before saving. */
export function validateLocation(
  t: (key: string) => string,
  territories: TerritoryRegion[],
  values: {
    region: string;
    mrc: string;
    places: LocationRow[];
  },
): LocationErrors {
  const req = t("expert.register.validation.required");
  const mrcsOf = (id: string) => territories.find((r) => r.region === id)?.mrcs ?? [];
  const errors: LocationErrors = {};
  if (!values.region) errors.head_office_region = req;
  if (!values.mrc) errors.head_office_mrc = req;
  else if (values.region && !mrcsOf(values.region).includes(values.mrc))
    errors.head_office_mrc = t("expert.register.validation.mrc_region");
  values.places.forEach((place, index) => {
    const e: { region?: string; mrc?: string } = {};
    // Both halves carry the place's own wording, matching the Django messages
    // — the head office has its own pair above.
    if (!place.region) e.region = t("expert.register.validation.place_region_required");
    if (!place.mrc) e.mrc = t("expert.register.validation.mrc_required");
    else if (place.region && !mrcsOf(place.region).includes(place.mrc))
      e.mrc = t("expert.register.validation.place_mrc_region");
    if (e.region || e.mrc) errors.places = { ...errors.places, [index]: e };
  });
  return errors;
}

export type ProvinceErrors = {
  serves_other_provinces?: string;
  other_provinces?: string;
};

/**
 * The provinces question of the service-range step: answered Oui/Non, and a
 * Oui names at least one province (registration_sync.other_provinces_error).
 */
export function validateProvinces(
  t: (key: string) => string,
  serves: boolean | null | undefined,
  provinces: string[],
): ProvinceErrors {
  const errors: ProvinceErrors = {};
  if (serves == null) errors.serves_other_provinces = t("expert.register.validation.required");
  else if (serves && provinces.length === 0)
    errors.other_provinces = t("expert.register.validation.provinces_required");
  return errors;
}

/**
 * What a chosen address suggestion changes on one location: its address, and
 * its région and MRC when the server resolved both to a pair the pickers offer.
 * Otherwise the région and MRC are left as the expert set them — an address
 * outside Québec, or one whose territory could not be resolved, says nothing
 * about them. An address the server could not confirm keeps the suggestion's
 * own text. Mirrors `fill()` in the Django app's address-autocomplete.js.
 */
export function locationAddressPatch(
  result: {
    confirmed: boolean;
    formatted_address: string;
    region_id: string | null;
    mrc: string;
  },
  suggestionText: string,
  territories: TerritoryRegion[],
): { address: string; region?: string; mrc?: string } {
  if (!result.confirmed) return { address: suggestionText };
  const address = result.formatted_address || suggestionText;
  const region = territories.find((r) => r.region === result.region_id);
  if (!region || !result.mrc || !region.mrcs.includes(result.mrc)) return { address };
  return { address, region: region.region, mrc: result.mrc };
}
